'use client';
import { addToCart } from "app/actions/cart.actions";
import { redirectToCheckout } from 'components/cart/actions';
import { useCart } from 'components/cart/cart-context';
import { DeleteItemButton } from 'components/cart/delete-item-button';
import { EditItemQuantityButton } from 'components/cart/edit-item-quantity-button';
import OpenCart from 'components/cart/open-cart';
import LoadingDots from 'components/loading-dots';
import Price from 'components/price';
import { Spinner } from 'flowbite-react';
import { DEFAULT_OPTION } from 'lib/constants';
import { ComponentProduct, Product, ProductVariant, ShopifyDiscount } from 'lib/shopify/types';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { startTransition, useEffect, useState } from 'react';
import { useFormStatus } from 'react-dom';
import { useModalStore } from 'stores/modalStore';
import { cn } from 'utils/cn';
import { CurrencyType, mapCurrencyToSign } from 'utils/mapCurrencyToSign';
import { AddonSelectPanel } from './addon-select-panel';

interface ProductFromBundle {
  bundleId: string;
  bundleHandle: string;
  productQuantity: number;
  product: ComponentProduct;
}

export function AddonCartOverview({
  productsFromBundles,
  discount,
  product,
  productDiscounts
}: {
  productsFromBundles: ProductFromBundle[];
  discount: number;
  product: Product;
  productDiscounts: ShopifyDiscount[];
}) {
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const router = useRouter();
  const pathname = usePathname();
  const { cart, updateCartItem } = useCart();
  const { variants, compareAtPriceRange, handle } = product;
  const { openModal, modals, closeModal } = useModalStore();
  const variant = variants.find((variant: ProductVariant) => variant.title === product.title);
  const defaultVariantId = variants.length === 1 ? variants[0]?.id : undefined;
  const selectedVariantId = variant?.id || defaultVariantId;
  const finalVariant = variants.find((variant) => variant.id === selectedVariantId)!;

   useEffect(() => {
        closeModal('upgradeHub')
        const isUpgraded = pathname.includes('upgraded')
        const isEssential = pathname.includes('essential')
        const isDowngrading = window.history.state?.isDowngrading

        if(isEssential && !isUpgraded && !isDowngrading){
            setTimeout(() => {
                openModal('upgradeHub')
            }, 3000)
        }

        return () => {
            if (isUpgraded) {
                window.history.replaceState({ ...window.history.state, isDowngrading: true }, '')
            }
        }
    }, [pathname, closeModal, openModal])

  useEffect(() => {
    if (!cart || cart?.lines.length === 0) {

      setLoading(true)
      startTransition(async() => {
        const {message : messageResponse} = await addToCart(handle)
        setLoading(false)
        setMessage(messageResponse)
      })
    }
  }, [cart, cart?.lines, pathname]);

  if(loading){
    const skeleton = 'w-full h-6 animate-pulse rounded bg-neutral-700 dark:bg-neutral-700';
    return (
        <div className="flex flex-col gap-4 w-full mt-8 pt-4 px-4">
            <h2 className='text-base font-semibold'>Loading Shopping Cart</h2>
            <div className={skeleton} />
            <div className={skeleton} />            
            <div className={skeleton} />            
            <div className={skeleton} />            
        </div>
    )
  }

  let totalSavings = 0;
  return (
    <div className="flex w-full flex-1 flex-col">
      <div className="flex flex-1 flex-col">
        <div className='pl-6 py-1 mt-4 flex justify-between items-center'>
            <div className='flex flex-row items-center'>
                <button className="pointer-events-none">
                    <OpenCart quantity={cart?.totalQuantity} />
                </button>
                <h1 className='text-base font-semibold ml-4'>My Shopping Cart</h1>
            </div>
            <AddonSelectPanel className=""/>
        </div>
        <div className="lg:relative flex flex-1 flex-col overflow-y-auto lg:px-4 pt-2">
          <div className="lg:absolute flex flex-col gap-2">
            {cart?.lines
              .sort((a, b) => {
                const isBundle = productsFromBundles.find(
                  (bundle) => bundle.bundleHandle === a.merchandise.product.handle
                );
                if (isBundle) {
                  return -1;
                }
                return 1;
              })
              .map((line) => {
                const {
                  merchandise: {
                    product: {
                      id,
                      title: fullTitle,
                      priceRange,
                      handle: cartLineHandle,
                      featuredImage
                    }
                  },
                  quantity
                } = line;
                const title = fullTitle.includes('System')
                  ? fullTitle.split('System')[0] + 'System'
                  : fullTitle;
                const filterProductBundles = productsFromBundles.filter(
                  (bundle) => bundle.bundleId === id
                );
                const isBundle = filterProductBundles.length > 0;
                const productDiscount = productDiscounts.find((discount) =>
                  discount.customerGets?.items?.collections?.edges.find(
                    (product) => product.node.handle === cartLineHandle
                  )
                );
                const discountByProductPercentage =
                  productDiscount?.customerGets?.value?.percentage ?? 0;
                const discountByProductAmount =
                  productDiscount?.customerGets?.value?.amount?.amount ?? 0;
                const productDiscountValue =
                  discountByProductPercentage > 0 ? discountByProductPercentage : 0;
                const price = (
                  Number(priceRange.minVariantPrice.amount) *
                  quantity *
                  (1 - productDiscountValue)
                ).toFixed(2);
                const discountedPrice = Number(line.cost.totalAmount.amount).toFixed(2);
                const compareAtPrice =
                  price !== discountedPrice
                    ? price
                    : false || +compareAtPriceRange?.maxVariantPrice?.amount;
                const compareAtPriceCurrency =
                  mapCurrencyToSign(
                    compareAtPriceRange?.maxVariantPrice?.currencyCode as CurrencyType
                  ) ?? '$';
                totalSavings += compareAtPrice ? +compareAtPrice - Number(discountedPrice) : 0;
                return (
                  <div key={id} className="flex flex-col rounded-lg bg-white px-4 py-4">
                    <div className="flex justify-between flex-row">
                      <div className="flex flex-col gap-2 md:items-center justify-between md:flex-row">
                        <Image src={featuredImage?.url} alt={title} width={100} height={100} />
                        <div className={cn('font-semibold md:ml-4', { '': isBundle })}>{title}</div>
                        <div className="ml-4">
                          {line.merchandise.title !== DEFAULT_OPTION &&
                          line.merchandise.title !== line.merchandise.product.title ? (
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                              -{line.merchandise.title}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="peer flex flex-col items-center justify-between py-2 has-[.loading]:hidden md:items-start">
                        <div className="flex flex-row">
                          {!!compareAtPrice && (
                            <del 
                              className="mr-2"
                              aria-label={`Original price: ${compareAtPriceCurrency}${compareAtPrice}`}
                            >
                              {compareAtPriceCurrency}
                              {compareAtPrice}
                            </del>
                          )}
                          <ins 
                            className="no-underline"
                            aria-label={`Current price: ${mapCurrencyToSign(
                              priceRange.minVariantPrice.currencyCode as CurrencyType
                            )}${discountedPrice}`}
                          >
                            {mapCurrencyToSign(
                              priceRange.minVariantPrice.currencyCode as CurrencyType
                            )}
                            {discountedPrice}
                          </ins>
                        </div>
                        {!isBundle && (
                          <div className="flex flex-row items-center">
                            <div className="mr-4">
                              <DeleteItemButton item={line} optimisticUpdate={updateCartItem} />
                            </div>
                            <EditItemQuantityButton
                              item={line}
                              type="minus"
                              optimisticUpdate={updateCartItem}
                            />
                            <p className="w-6 text-center">
                              <span className="w-full text-sm">{quantity}</span>
                            </p>
                            <EditItemQuantityButton
                              item={line}
                              type="plus"
                              optimisticUpdate={updateCartItem}
                            />
                          </div>
                        )}
                      </div>
                      <div className={'hidden peer-has-[.loading]:block'}>
                        <Spinner aria-label="Large spinner" size="lg" />
                      </div>
                    </div>
                    {isBundle && (
                      <div className="mt-4 flex flex-col bg-[#FAFAFA] rounded-lg">
                        {filterProductBundles
                          .sort((a, b) => {
                            if (a.product.handle === 'hubkeypad') return -1;
                            if (b.product.handle === 'hubkeypad') return 1;
                            if (a.product.handle === 'display-hub') return -1;
                            if (b.product.handle === 'display-hub') return 1;
                            if (a.product.handle === 'home-defense-kit') return 1;
                            if (b.product.handle === 'home-defense-kit') return -1;
                            return 0;
                          })
                          .map((bundle) => {
                            return (
                              <div
                                key={bundle.product.id}
                                className="ml-2 mt-2 flex justify-between"
                              >
                                <div>
                                  <span className="font-bold">{bundle.productQuantity} x</span>{' '}
                                  {bundle.product.title}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
        <div className="mt-2 flex flex-col lg:px-4">
          <div className="flex justify-end">
            <div className="w-full text-sm text-neutral-500 dark:text-neutral-400">
              <div className="mb-1 flex items-center justify-between border-b border-neutral-200 pb-1 pt-1 text-lg dark:border-neutral-700">
                <p>Total Savings</p>
                <Price
                  className="text-right font-bold text-black dark:text-white"
                  amount={totalSavings.toFixed(2)}
                  currencyCode={cart?.cost.subtotalAmount.currencyCode ?? 'USD'}
                  discount={discount}
                />
              </div>
              <div className="mb-1 flex items-center justify-between border-b border-neutral-200 pb-1 pt-1 text-lg dark:border-neutral-700">
                <p>Shipping</p>
                <p className="text-right">Calculated at checkout</p>
              </div>
              <div className="mb-1 flex items-center justify-between border-b border-neutral-200 pb-1 pt-1 text-lg dark:border-neutral-700">
                <p>Subtotal</p>
                <Price
                  className="text-right text-base font-bold text-black dark:text-white"
                  amount={cart?.cost.subtotalAmount.amount ?? '0'}
                  currencyCode={cart?.cost.subtotalAmount.currencyCode ?? 'USD'}
                  discount={discount}
                />
              </div>
            </div>
          </div>
          <div className="mt-2 flex items-center rounded-lg bg-white lg:bg-[#f5f5f5] p-4">
            <div className="mr-4">
              <Image src="/icons/30-day-return.png" alt="30-day return" width={60} height={60} className='w-[60px] h-auto lg:w-[90px]' />
            </div>
            <div>
              <h4 className="text-xl font-medium">30-DAY RISK-FREE GUARANTEE</h4>
              <p className="text-sm">
                If for any reason you don't love any part of your Frontpoint system, simply return
                it to us for a full refund.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div>
        <form action={redirectToCheckout} className="mt-4 flex px-4 pb-4">
          <CheckoutButton />
        </form>
      </div>
    </div>
  );
}

function CheckoutButton() {
  const { pending } = useFormStatus();

  return (
    <button
      className="phone-button flex min-h-10 w-full min-w-24 flex-nowrap items-center justify-center rounded-lg border-2 bg-fp-green p-4 text-white hover:border-2 hover:border-fp-green hover:bg-white hover:text-fp-green"
      type="submit"
      disabled={pending}
    >
      {pending ? <LoadingDots className="bg-white" /> : 'Checkout'}
    </button>
  );
}
